/**
 * Maps a public-app catalog entity's annotations to the public-app-hosting
 * template formData used for redeploy.
 *
 * Mirrors the pattern from microappRedeployForm.ts for internal apps.
 */

import { Entity } from '@backstage/catalog-model';

export type PublicAppRedeployFormData = Record<string, unknown>;

const DEFAULT_CONTAINER_PORT = 3001;
const DEFAULT_STORAGE_SIZE_GB = 10;

/**
 * Builds template formData from a public app entity's annotations.
 * Returns null when the entity is not a hosted public app.
 *
 * @param currentUsers - current members of the app's pub-{app} access group,
 *   when they could be loaded. They pre-fill the Individual Users picker and
 *   are also sent as `previousAllowedUsers`, the snapshot the backend diffs
 *   against to find users removed on the form. When omitted, no snapshot is
 *   sent and the redeploy can only add access.
 */
export function buildPublicAppRedeployFormData(
  entity: Entity,
  currentUsers?: string[],
  dbGroups?: string[],
): PublicAppRedeployFormData | null {
  const annotations = entity.metadata.annotations ?? {};

  const sourceLocation = annotations['backstage.io/source-location'];
  if (!sourceLocation) return null;

  const appType = annotations['skystage.wellsky.com/type'];
  if (appType !== 'public-microapp') return null;

  const githubRepoUrl = sourceLocation.replace(/^url:/, '');

  const containerPortStr = annotations['microapp/port'];
  const containerPort = containerPortStr
    ? parseInt(containerPortStr, 10)
    : DEFAULT_CONTAINER_PORT;

  const ownerEmailsStr = annotations['microapp/owners'] ?? '';
  const ownerEmails = ownerEmailsStr
    ? ownerEmailsStr
        .split(',')
        .map(e => e.trim().toLowerCase())
        .filter(e => e.length > 0)
    : [];

  // Carry the current client groups through the redeploy. The backend rewrites
  // backstage.yaml from the submitted allowedGroups, so leaving this empty
  // would wipe skystage.io/allowed-group-names on every redeploy.
  const annotationGroups = (
    annotations['skystage.io/allowed-group-names'] ?? ''
  )
    .split(',')
    .map(g => g.trim())
    .filter(g => g.length > 0);
  // Prefer groups from the DB (passed by the caller after a live GET) so the
  // redeploy pre-fills the correct groups even before the catalog re-indexes.
  const allowedGroups =
    dbGroups && dbGroups.length > 0 ? dbGroups : annotationGroups;

  // IDP-1277: carry persistent storage through the redeploy (same parsing as
  // microappRedeployForm). The size field only lets it grow from here.
  const needsPersistentStorage =
    annotations['microapp/persistentStorage'] === 'true';
  const storageSizeStr = annotations['microapp/storageSize'] ?? '';
  const parsedStorageSize = parseInt(storageSizeStr.replace('Gi', ''), 10);
  const storageSize = isNaN(parsedStorageSize)
    ? DEFAULT_STORAGE_SIZE_GB
    : parsedStorageSize;

  // Carry existing secret key names so the redeploy form shows which secrets the
  // app currently has. Values are NOT pre-filled (they live in GCP Secret Manager
  // and must not be surfaced here); the user must re-enter them to rotate, or
  // leave them blank and the template will preserve the existing secret versions.
  // The annotation 'microapp/gsmSecretKeys' is written by the deploy scaffolder
  // action as a comma-separated list of env var key names (e.g. "DB_PASS,API_KEY").
  const secretKeysStr = annotations['microapp/gsmSecretKeys'] ?? '';
  const secretKeys = secretKeysStr
    .split(',')
    .map(k => k.trim())
    .filter(k => k.length > 0);
  const enableSecrets = secretKeys.length > 0;

  return {
    githubRepoUrl: { value: githubRepoUrl },
    containerPort,
    ownerEmails,
    allowedGroups,
    // Snapshot of the groups shown when the form opened. The backend diffs the
    // submitted groups against it, so only groups the owner actually removed
    // on the form are revoked (never ones added elsewhere since).
    previousAllowedGroups: allowedGroups,
    ...(currentUsers && {
      allowedUsers: currentUsers,
      previousAllowedUsers: currentUsers,
    }),
    needsPersistentStorage,
    ...(needsPersistentStorage && { storageSize }),
    enableSecrets,
    // Pre-populate key names without values. A blank value means "keep the
    // existing secret version" when the template processes the redeploy.
    ...(enableSecrets && {
      secrets: secretKeys.map(key => ({ key, value: '' })),
    }),
    actionType: 'redeploy',
  };
}
